import { useCallback } from 'react'

import { importFitFile } from '@/lib/fit-import'
import { useActivityStore, type ImportedFile } from '@/store/activity-store'
import { useNotificationStore } from '@/store/notification-store'

/**
 * Returns a stable function that imports a batch of files. Each file is
 * decoded and reported independently — one bad file never blocks or
 * fails the rest of the batch, and every file gets its own specific
 * success/warning/error notification rather than a single generic
 * "import failed" message.
 *
 * Every successfully decoded file from one call is added to the store in
 * a single `importActivities` call, so the whole import is one global
 * undo step ("Import 2 files"), in the order the files were given.
 */
export function useFileImport(): (files: FileList | File[]) => Promise<void> {
  const importActivities = useActivityStore((s) => s.importActivities)
  const notify = useNotificationStore((s) => s.notify)

  return useCallback(
    async (files: FileList | File[]) => {
      const outcomes = await Promise.all(Array.from(files).map((file) => importFitFile(file)))

      const decoded: ImportedFile[] = []
      for (const outcome of outcomes) {
        if (outcome.status !== 'failed') {
          const { activity, fileName, fieldDefsByMesgNum } = outcome
          decoded.push({ fileName, activity, fieldDefsByMesgNum })
        }
      }
      importActivities(decoded)

      for (const outcome of outcomes) {
        if (outcome.status === 'failed') {
          notify({
            variant: 'error',
            title: `Could not read "${outcome.fileName}"`,
            description: outcome.message,
          })
        } else if (outcome.activity.decodeErrors.length > 0) {
          notify({
            variant: 'error',
            title: `"${outcome.fileName}" loaded with errors`,
            description: outcome.activity.decodeErrors.map((e) => e.message).join('; '),
          })
        } else {
          notify({ variant: 'success', title: `"${outcome.fileName}" loaded` })
        }
      }
    },
    [importActivities, notify],
  )
}
